import React, { useState } from "react";
import api from "@/lib/api";
import { Flame, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline coaching-inquiry form used on the landing page (below the fold).
 * Mirrors the standalone /contact page but is compact for landing conversion:
 *   name → email → goals → send. Optional phone stays hidden until interacted.
 */
const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
];

export default function LandingContact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    state: "",
    goals: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.goals) {
      return toast.error("Name, email and goals are required");
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        phone: form.phone || null,
        state: form.state || null,
      });
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) toast.error(msg[0]?.msg || "Invalid input");
      else toast.error(msg || "Could not submit — try again in a moment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border-t border-obsidian-600 bg-obsidian-900" data-testid="landing-contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="overline text-blaze mb-3">/ Talk to a coach</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase leading-[0.95]">
            Not sure where to start?<br />
            <span className="text-blaze">Book a chat.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-md">
            Drop your details and a Ripple Effect coach will personally reach out within 24 hours —
            free, no obligation. Skip the app trial and let a human map your first month.
          </p>
          <div className="mt-6 space-y-2 text-xs text-zinc-500 uppercase tracking-widest">
            <div className="flex items-center gap-2"><Check className="h-3 w-3 text-blaze" /> No spam ever</div>
            <div className="flex items-center gap-2"><Check className="h-3 w-3 text-blaze" /> Straight to a real coach</div>
            <div className="flex items-center gap-2"><Check className="h-3 w-3 text-blaze" /> Reply within 24 hours</div>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="obs-card" data-testid="landing-contact-card">
            {sent ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto rounded-sm border border-blaze/60 bg-blaze/10 grid place-items-center mb-4">
                  <Check className="h-6 w-6 text-blaze" />
                </div>
                <h3 className="font-heading text-3xl uppercase">Got it — we&apos;ll reach out.</h3>
                <p className="text-zinc-400 text-sm mt-2">
                  A coach will be in touch at <span className="text-zinc-100">{form.email}</span> within 24h.
                </p>
                <button
                  type="button"
                  data-testid="landing-contact-reset"
                  onClick={() => { setSent(false); setForm({ full_name: "", email: "", phone: "", state: "", goals: "" }); }}
                  className="ghost-btn px-4 py-2 text-xs uppercase tracking-widest mt-4"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="overline mb-1">Full name *</div>
                    <input
                      data-testid="landing-contact-full-name"
                      type="text"
                      required
                      value={form.full_name}
                      onChange={(e) => update("full_name", e.target.value)}
                      className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                    />
                  </label>
                  <label className="block">
                    <div className="overline mb-1">Email *</div>
                    <input
                      data-testid="landing-contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                    />
                  </label>
                </div>

                <label className="block">
                  <div className="overline mb-1">Your goals *</div>
                  <textarea
                    data-testid="landing-contact-goals"
                    required
                    rows={3}
                    value={form.goals}
                    onChange={(e) => update("goals", e.target.value)}
                    placeholder="e.g. Lose 15 lb & build strength before my wedding in October."
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  />
                </label>

                {!showMore && (
                  <button
                    type="button"
                    data-testid="landing-contact-more"
                    onClick={() => setShowMore(true)}
                    className="text-[11px] uppercase tracking-widest text-blaze hover:underline"
                  >
                    + Add phone & state (optional)
                  </button>
                )}
                {showMore && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="overline mb-1">Phone</div>
                      <input
                        data-testid="landing-contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                      />
                    </label>
                    <label className="block">
                      <div className="overline mb-1">State</div>
                      <select
                        data-testid="landing-contact-state"
                        value={form.state}
                        onChange={(e) => update("state", e.target.value)}
                        className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                      >
                        <option value="">Select…</option>
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="landing-contact-submit"
                  disabled={loading}
                  className="blaze-btn w-full py-3 text-sm mt-2 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <Flame className="h-4 w-4" />
                  {loading ? "Sending…" : "Send inquiry"}
                </button>
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest text-center pt-1">
                  Prefer to explore first? <a href="/auth/signup" className="text-blaze hover:underline">Start free →</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
